import { useEffect, useState } from 'react'
import { MdClose, MdOutlineAddBox, MdOutlineIosShare } from 'react-icons/md'

import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useGetUser from '@/libs/hooks/use-get-user'
import useToastNotification from '@/libs/hooks/use-toast-notification'
import { setPostponeInstallDate } from '@/libs/store/slice/ui-slice'

type TInstallBanner = {
  keepShow?: boolean
  disableClose?: boolean
}

export default function InstallBanner({
  keepShow = false,
  disableClose = false,
}: Readonly<TInstallBanner>) {
  const { show } = useToastNotification()
  const dispatch = useAppDispatch()
  const { postponeInstallDate } = useAppSelector((state) => state.ui)
  const { partnerId, partnerIdNonIntegration } = useAppSelector((state) => state.user)

  const [deferredPrompt, setDeferredPrompt] = useState<Event | null>(null)
  const [isStandalone, setIsStandalone] = useState<boolean>(false)
  const [isInstalled, setIsInstalled] = useState<boolean>(false)

  const { queryInfo } = useGetUser()

  const currentDate = new Date()
  const hasPartnerId = partnerId || partnerIdNonIntegration
  const hideBanner =
    !keepShow && postponeInstallDate && new Date(postponeInstallDate) >= currentDate

  const isUnsupportedBrowser = () => {
    return /Firefox|MSIE|Trident|QQBrowser|Baidu|KaiOS/.test(navigator.userAgent)
  }

  const isWebView = () => {
    const criteria =
      !('Notification' in window) &&
      (!/Safari|Chrome/.test(navigator.userAgent) || /wv/.test(navigator.userAgent))

    return criteria
  }

  const isAndroid = () => {
    return /Android/.test(navigator.userAgent) && !isUnsupportedBrowser() && !isWebView()
  }

  const isIos = () => {
    return (
      /iPhone|iPod/.test(navigator.userAgent) &&
      !(window as any).MSStream &&
      !isUnsupportedBrowser() &&
      !isWebView()
    )
  }

  const isIpad = () => {
    return (
      /iPad/.test(navigator.userAgent) &&
      !(window as any).MSStream &&
      !isUnsupportedBrowser() &&
      !isWebView()
    )
  }

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e)
    }

    const checkIfInstalled = async () => {
      if ('getInstalledRelatedApps' in navigator) {
        const relatedApps = await (navigator as any).getInstalledRelatedApps()
        if (relatedApps.some((app: any) => app.platform === 'webapp')) {
          setIsInstalled(true)
        }
      }
    }

    const checkIfStandalone = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsStandalone(true)
      }
    }

    checkIfStandalone()
    checkIfInstalled()

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleHideBanner = () => {
    const postponeDate = new Date(currentDate.getTime() + 3 * 60 * 60 * 1000)
    dispatch(setPostponeInstallDate(postponeDate))
  }

  const handleInstall = async () => {
    if (isIos() || isIpad()) {
      return show({
        disableIcon: true,
        type: 'info',
        title: '',
        content: (
          <div className='flex flex-col'>
            <div className='flex items-center'>
              <span>1. Install on your {isIos() ? 'iPhone' : 'iPad'}; tap</span>
              <MdOutlineIosShare className='mx-1 h-6 w-6 text-pop-blue-400' />
            </div>
            <div className='flex items-center'>
              <span>2. and then choose;</span>
              <MdOutlineAddBox className='mx-1 h-6 w-6 text-pop-blue-400' />
            </div>
            <div className='flex items-center'>
              <span>3. “Add to Home Screen”</span>
            </div>
          </div>
        ),
        autoclose: false,
      })
    }

    if (deferredPrompt) {
      ;(deferredPrompt as any).prompt()
      await (deferredPrompt as any).userChoice
      return setDeferredPrompt(null)
    }
  }

  const handleOpen = () => {
    window.open(process.env.NEXT_PUBLIC_APP_URL, '_blank')
  }

  if (hasPartnerId || queryInfo?.isGuest || hideBanner || (!deferredPrompt && isStandalone)) {
    return null
  }

  return (
    <>
      {(isAndroid() || isIos() || isIpad()) && (
        <div className='flex flex-row items-center gap-3 bg-gradient-to-r from-pop-blue-700 to-pop-blue-600 px-5 py-2'>
          {!disableClose && (
            <MdClose className='h-6 w-6 cursor-pointer text-white' onClick={handleHideBanner} />
          )}
          <div className='mr-auto'>
            <h1 className='text-sm font-semibold text-white'>Responova</h1>
            <h1 className='text-xs font-medium text-white'>Use app to access survey faster</h1>
          </div>
          <button
            className='flex flex-row items-center rounded-full bg-white px-4 py-[10px] text-sm font-medium text-pop-blue-700'
            onClick={isInstalled ? handleOpen : handleInstall}
          >
            {isInstalled ? 'Open' : 'Install'}
          </button>
        </div>
      )}
    </>
  )
}
